import { Trash2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/components/Tooltip';

interface NodeDeleteButtonProps {
  onDelete: () => void;
  disabled: boolean;
  nodeId: string; // For data-testid and aria-label
}

/**
 * NodeDeleteButton - Trash icon button for deleting nodes
 *
 * Renders a grey trash icon that becomes red on hover (when enabled).
 * Disabled state shown with reduced opacity and cursor-not-allowed.
 * Click event stops propagation to prevent parent button toggle.
 */
export const NodeDeleteButton = ({
  onDelete,
  disabled,
  nodeId,
}: NodeDeleteButtonProps) => {
  const message = disabled
    ? 'Only the last node may be deleted'
    : 'Delete this node and its relationship';
  return (
    <Tooltip title={message}>
      <button
        onClick={e => {
          e.stopPropagation(); // Don't toggle node expansion
          onDelete();
        }}
        disabled={disabled}
        className={twMerge(
          'rounded p-1 transition-colors duration-200',
          disabled
            ? 'cursor-not-allowed opacity-40'
            : 'text-gray-400 hover:bg-red-50 hover:text-red-500'
        )}
        aria-label={message}
        data-testid={`node-delete-button-${nodeId}`}
      >
        <Trash2 size={16} />
      </button>
    </Tooltip>
  );
};
